import {MessageCodes} from "../MessageCodes";
import ResultTable from "../../../components/table/ResultTable";
import ResultText from "../ResultText";
import CheckCSName from "../CheckCSName";
import EmptyLine from "../EmptyLine";
import ResultHeader from "../../../components/table/ResultHeader";
import ResultRow from "../../../components/table/ResultRow";
import ResultHeaderCell from "../../../components/table/ResultHeaderCell";
import ResultBody from "../../../components/table/ResultBody";
import ResultBodyCell from "../../../components/table/ResultBodyCell";
import {useUser} from "../../../context/UserContext";
import HecRasHelper from "../../../helpers/HecRasHelper";
import ResultView from "../../../components/ResultView";

export function B26({data, message_key}) {
    const {settings} = useUser()

    return (
        <>
            {
                data?.result?.map(row => (
                    <ResultView error_id={row?.error_id} message_key={message_key}>
                        <Description row={row} settings={settings} globalVariables={data?.global_variables} />
                    </ResultView>
                ))
            }
        </>
    )
}

const Description = ({row, settings}) => {
    const getCsName = (row) => {
        if(row.highlight) {
            return (
                <div style={{color: 'red'}}>
                    {row.cross_section}
                </div>
            )
        }

        return row.cross_section
    }

    switch (row.error_id) {
        case MessageCodes.Internal.XS05.error_id: {
            const maxDsrl = (row?.max_dsrl !== undefined && row?.max_dsrl !== null)
                ? Number(row.max_dsrl).toFixed(2)
                : settings?.thresholds?.max_dsrl

            const e_times = row?.e_times;

            return (
                <>
                    <ResultText>
                        The following {row?.data?.length} cross sections satisfy all the listed conditions for the 100yr (1%) profile. This may indicate the need for additional cross sections downstream.  <EmptyLine height={1} />
                        {'\t'}1.	The Median Top Width of 100yr profile is {maxDsrl} (ft). The channel DS reach length is more than {e_times} x the Median Top Width.  <EmptyLine height={1} />
                        {'\t'}2.	The head loss is greater than {settings?.thresholds?.head_loss} ft. <EmptyLine height={1} />
                        {'\t'}3.	The conveyance ratio is less than 0.7 or greater than 1.3. <EmptyLine height={1} />
                        {'\t'}4.	The change in velocity head compared to the next DS cross section is more than {settings?.thresholds?.velocity_head} ft.
                        <EmptyLine />
                    </ResultText>
                    <ResultTable data={row?.data?.map(r => ({csName: r.cross_section, csNameVal: r.cross_section}))}>
                        <ResultHeader>
                            <ResultRow>
                                <ResultHeaderCell style={{textAlign: 'left', width: HecRasHelper.getWidth(15)}}>River Station</ResultHeaderCell>
                                <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>DS RL (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>Headloss (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(12)}}>Conv. Ratio (ft)</ResultHeaderCell>
                                <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(16)}}>Delta Vel Head (ft)</ResultHeaderCell>
                            </ResultRow>
                        </ResultHeader>
                        <ResultBody>
                            {
                                (row.data || [{}]).map(r => {
                                    return (
                                        <ResultRow>
                                            <ResultBodyCell style={{textAlign: 'left'}}><CheckCSName csName={getCsName(r)} csNameVal={r.cross_section} /></ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right'}}>{r.dsrl ? Math.round(r?.dsrl) : '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right'}}>{r.headloss ?? '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right'}}>{r.conv_ratio ?? '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{textAlign: 'right'}}>{r.vel_head ?? '-'}</ResultBodyCell>
                                        </ResultRow>
                                    )
                                })
                            }
                        </ResultBody>
                    </ResultTable>
                </>
            )
        }
        case MessageCodes.Internal.XS05A.error_id: {
            const maxDsrl = (row?.max_dsrl !== undefined && row?.max_dsrl !== null)
                ? Number(row.max_dsrl).toFixed(2)
                : settings?.thresholds?.max_dsrl

            const e_times = row?.e_times;

            const profiles = Array.from(new Set(row.data.map(x => x.profile)))
            return profiles.map(p => {
                const cs_list = row.data.filter(x => x.profile === p)
                if(cs_list.length === 0) {
                    return ''
                }

                return (
                    <>
                        <ResultText>
                            The Median Top Width of 100yr profile is {maxDsrl} (ft). The channel DS reach length is more than {e_times} x the Median Top Width at the following {cs_list?.map(x => x.cross_section)?.length} cross sections. This may indicate the need for additional cross sections downstream.
                        </ResultText>
                        <ResultText><CheckCSName csNameList={cs_list?.map(x => x.cross_section)} /></ResultText>
                        <EmptyLine />
                    </>
                )
            })
        }
        case MessageCodes.Internal.XS05B.error_id: {
            const profiles = Array.from(new Set(row.data.map(x => x.profile)))
            return profiles.map(p => {
                const cs_list = row.data.filter(x => x.profile === p)
                if(cs_list.length === 0) {
                    return ''
                }

                return (
                    <>
                        <ResultText>
                            For the {p} profile, the energy loss is greater than {settings?.thresholds?.head_loss || '{headloss}'} ft between the current and next downstream cross section at the following {cs_list?.length} cross sections. This may indicate the need for additional cross sections downstream.
                        </ResultText>
                        <ResultText><CheckCSName csNameList={cs_list?.map(x => x.cross_section)} /></ResultText>
                        <EmptyLine />
                    </>
                )
            })
        }
        case MessageCodes.Internal.XS05C.error_id: {
            const profiles = Array.from(new Set(row.data.map(x => x.profile)))
            return profiles.map(p => {
                const cs_list = row.data.filter(x => x.profile === p)
                if(cs_list.length === 0) {
                    return ''
                }

                return (
                    <>
                        <ResultText>
                            For the {p} profile, the conveyance ratio (conveyance divided by next downstream conveyance) is less than {1-(settings?.thresholds?.conv_ratio || 0.3)} or greater than {1+(settings?.thresholds?.conv_ratio || 0.3)} at the following {cs_list?.length} cross sections. This may indicate the need for additional cross sections downstream.
                        </ResultText>
                        <ResultText>
                            <CheckCSName csNameList={cs_list?.map(x => x.cross_section)} />
                        </ResultText>
                    </>
                )
            })
        }
        case MessageCodes.Internal.XS05D.error_id: {
            const profiles = Array.from(new Set(row.data.map(x => x.profile)))
            return profiles.map(p => {
                const cs_list = row.data.filter(x => x.profile === p)
                if(cs_list.length === 0) {
                    return ''
                }

                return (
                    <>
                        <ResultText>
                            For the {p} profile, the velocity head has changed by more than {1-(settings?.thresholds?.velocity_head || 0.5)} ft at the following {cs_list?.length} cross sections. This may indicate the need for additional cross sections downstream.
                        </ResultText>
                        <ResultText>
                            <CheckCSName csNameList={cs_list?.map(x => x.cross_section)} />
                        </ResultText>
                    </>
                )
            })
        }
    }
}